import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import cx from 'classnames';
import s from './Register.css';
import { register, changeForm, pageUnload } from '../../actions';
import FormComponent from '../../components/FormComponent';
import Form from '../../components/Form';
import { USER_REGISTER_FAILURE } from '../../constants';
import history from '../../history';
import blankImage from '../../../public/icons/no-image-available.jpg';

const mapStateToProps = state => ({
  formState: state.auth.formState,
  isLoading: state.auth.isFetching,
  errorMessage: state.auth.errorMessage
});

const mapDispatchToProps = {
  onFormSubmit: () => async (dispatch, getState) => {
    const { formState } = getState().auth;
    if (formState.password !== formState.confirmPassword) {
      dispatch({
        type: USER_REGISTER_FAILURE,
        payload: {
          response: {
            errorMessage: 'Passwords must match.'
          }
        }
      });
    } else {
      const res = await dispatch(register(formState));
      if (!res.error) {
        window.toastr.success('You are successfully registered now!');
        history.push('/login');
      }
    }
  },
  onChange: event => (dispatch, getState) => {
    event.preventDefault();
    const { formState } = getState().auth;
    if (event.target.type === 'file') {
      const file = event.target.files[0];
      // limit < 1mb
      if (file.size > 1000000) {
        dispatch({
          type: USER_REGISTER_FAILURE,
          payload: {
            response: {
              errorMessage: 'Please upload a picture smaller than 1 MB.'
            }
          }
        });
      } else {
        const reader = new FileReader();
        reader.onload = upload => {
          dispatch(pageUnload()); // clear error message
          dispatch(
            changeForm({ ...formState, image: upload.target.result, file })
          );
        };
        reader.readAsDataURL(file);
      }
    } else if (event.target.name === 'kidsCount') {
      const kidsCount = parseInt(event.target.value, 10);
      dispatch(
        changeForm({
          ...formState,
          [event.target.name]: kidsCount,
          kidsAges: Array(kidsCount).fill(0)
        })
      );
    } else if (event.target.name.startsWith('kid')) {
      const index = parseInt(event.target.name.substr(3), 10); // kid1 is resulted in index 1 for that kid
      const kidsAges = [
        ...formState.kidsAges.slice(0, index),
        parseInt(event.target.value, 10),
        ...formState.kidsAges.slice(index + 1)
      ];
      dispatch(
        changeForm({
          ...formState,
          kidsAges
        })
      );
    } else {
      dispatch(
        changeForm({
          ...formState,
          [event.target.name]: event.target.value
        })
      );
    }
  },
  onUnload: () => dispatch => {
    dispatch(pageUnload());
  }
};

const FormType = {
  username: PropTypes.string,
  password: PropTypes.string.isRequired,
  firstname: PropTypes.string.isRequired,
  lastname: PropTypes.string.isRequired
};

class Register extends React.Component {
  static contextTypes = { fetch: PropTypes.func.isRequired };

  static propTypes = {
    title: PropTypes.string.isRequired,
    isLoading: PropTypes.bool.isRequired,
    errorMessage: PropTypes.string.isRequired,
    formState: PropTypes.shape(FormType).isRequired,
    onFormSubmit: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onUnload: PropTypes.func.isRequired
  };

  state = {
    recaptchaFailed: false,
    recaptchaCompleted: false
  };

  componentDidMount() {
    setTimeout(() => {
      window.grecaptcha.render('g-recaptcha', {
        sitekey: '6Ld700YUAAAAAECUF4wYras641n5pZSopQBoEBOL',
        callback: this.grecaptchaCallback
      });
    }, 300);
  }

  componentWillUnmount() {
    this.props.onUnload();
  }

  grecaptchaCallback = async response => {
    const res = await this.context.fetch('/api/recaptcha', {
      method: 'POST',
      body: JSON.stringify({
        'grecaptcha-response': response
      })
    });

    if (res.ok) {
      this.setState({
        recaptchaFailed: false,
        recaptchaCompleted: true
      });
    }
  };

  handleFormSubmit = event => {
    event.preventDefault();
    if (
      !this.state.recaptchaCompleted &&
      window.grecaptcha.getResponse() === ''
    ) {
      this.setState({
        recaptchaFailed: true
      });
      return;
    }
    this.props.onFormSubmit();
  };

  render() {
    const { title, formState, isLoading, errorMessage, onChange } = this.props;
    return (
      <div className="container-fluid">
        <div className="row">
          <div className={s.panel}>
            <Form
              title={title}
              isLoading={isLoading}
              loadingMessage="Registering..."
              errorMessage={errorMessage}
              onFormSubmit={this.handleFormSubmit}
            >
              <div className={s.field}>
                <i className={cx(s.icon, 'fa', 'fa-envelope')} />
                <FormComponent
                  name="username"
                  type="email"
                  pattern="\S+@\S+\.\S+"
                  title="Eight a valid email address"
                  value={formState.username}
                  onChange={onChange}
                  placeholder="Email"
                  required
                />
                <div className={s.iconPlaceholder} />
              </div>
              <div className={s.field}>
                <i className={cx(s.icon, 'fa', 'fa-lock')} />
                <FormComponent
                  name="password"
                  type="password"
                  pattern="(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).{8,}"
                  title="Eight or more characters with at least 1 lower case, 1 upper case, and 1 number"
                  value={formState.password}
                  onChange={onChange}
                  placeholder="Password"
                  required
                />
                <div className={s.iconPlaceholder} />
              </div>
              <div className={s.field}>
                <div className={s.iconPlaceholder} />
                <FormComponent
                  name="confirmPassword"
                  type="password"
                  pattern="(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).{8,}"
                  title="Eight or more characters with at least 1 lower case, 1 upper case, and 1 number"
                  value={formState.confirmPassword}
                  onChange={onChange}
                  placeholder="Confirm password"
                  required
                />
                <div className={s.iconPlaceholder} />
              </div>
              <div className={s.field}>
                <i className={cx(s.icon, 'fa', 'fa-vcard')} />
                <FormComponent
                  name="firstName"
                  value={formState.firstName}
                  onChange={onChange}
                  placeholder="First name"
                  required
                />
                <div className={s.iconPlaceholder} />
              </div>
              <div className={s.field}>
                <div className={s.iconPlaceholder} />
                <FormComponent
                  name="lastName"
                  value={formState.lastName}
                  onChange={onChange}
                  placeholder="Last name"
                  required
                />
                <div className={s.iconPlaceholder} />
              </div>
              <div className={s.field}>
                <i className={cx(s.icon, 'fa', 'fa-user')} />
                <select
                  className={`form-control mb-1 ${s.genderSelect}`}
                  name="gender"
                  value={formState.gender}
                  onChange={onChange}
                  required
                >
                  <option value="" hidden>
                    Gender
                  </option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="others">Others</option>
                </select>
                <div className={s.iconPlaceholder} />
              </div>
              <div className={s.field}>
                <i className={cx(s.icon, 'fa', 'fa-phone')} />
                <FormComponent
                  name="phone"
                  value={formState.phone}
                  onChange={onChange}
                  placeholder="Phone"
                />
                <div className={s.iconPlaceholder} />
              </div>
              <div className={s.field}>
                <i className={cx(s.icon, 'fa', 'fa-home')} />
                <FormComponent
                  name="address"
                  value={formState.address}
                  onChange={onChange}
                  placeholder="Address"
                />
                <div className={s.iconPlaceholder} />
              </div>
              <div className={s.field}>
                <i className={cx(s.icon, 'fa', 'fa-user')} />
                <select
                  className={`form-control mb-1 ${s.genderSelect}`}
                  name="kidsCount"
                  value={formState.kidsCount}
                  onChange={onChange}
                  required
                >
                  <option value="" hidden>
                    Number of Kids
                  </option>
                  <option value="0">0</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5+</option>
                </select>
                <div className={s.iconPlaceholder} />
              </div>
              {parseInt(formState.kidsCount, 10) < 5 && (
                <div className={s.field}>
                  {parseInt(formState.kidsCount, 10) > 0 && <label>Age: </label>}
                  {Array(parseInt(formState.kidsCount, 10))
                    .fill(0)
                    .map((_, idx) => (
                      <input
                        className={s.kidsAgeField}
                        type="number"
                        min="0"
                        max="40"
                        name={`kid${idx}`}
                        onChange={onChange}
                      />
                    ))}
                </div>
              )}
              <div className="row">
                <div className="col align-self-center">
                  <div className={s.uploadButtonWrapper}>
                    <div className={s.glyphWrapper}>
                      <i className="fa fa-camera" aria-hidden="true" />
                    </div>
                    <div className={s.imageOverlay}>
                      {formState.image ? (
                        <img
                          alt="profile pic"
                          src={formState.image}
                          className="img-thumbnail"
                        />
                      ) : (
                        <img
                          src={blankImage}
                          className="img-thumbnail"
                          width="100"
                          height="100"
                        />
                      )}
                    </div>
                    <input
                      type="file"
                      onChange={onChange}
                      name="image"
                      accept="image/*"
                    />
                  </div>
                </div>
              </div>
              <div className={this.state.recaptchaFailed ? s.recaptcha : null}>
                <div id="g-recaptcha" />
                {this.state.recaptchaFailed
                  ? <p className={s.recaptchaFailedMessage}>
                      Please verify that you are not a robot.
                    </p>
                  : null}
              </div>
            </Form>
          </div>
        </div>
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(s)(Register)
);



// WEBPACK FOOTER //
// ./src/routes/register/Register.js